import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostBinding,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Rule, Stage } from '../../../models/rule.model';
import { Store } from '@ngrx/store';
import {
  selectIsCreateOrUpdateRuleOk,
  selectLoadingUpdateRule,
  selectRuleUpdateErrorMessage,
  selectRuleCreating,
  selectRuleCreateErrorMessage,
} from '../../../store/selectors/rules.selector';
import { CriticalityLevel } from '../../../../enum/criticality-level.enum';
import { UpdateRuleRequest } from '../../../models/requests/update-rule.request';
import { CreateRuleRequest } from '../../../models/requests/create-rule.request';
import * as ruleActions from '../../../store/actions/rules.actions';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { criticalityOptions } from '../../constants/criticality-option.constans';
import { showMessage } from '../../../store/common/app.action';
import { Actions, ofType } from '@ngrx/effects';
import { v4 as uuidv4 } from 'uuid';
import { ERROR_KEYS } from '../../constants/error-keys.constant';
import { TIME_FRAME } from '../../constants/filters.constants';
import { computeThresholdDate } from '../../../../utils/date-utils';
import { Plant } from '../../../models/plant.model';
import { selectPlants } from '../../../store/selectors/plant.selector';
import { LocalisationStepComponent } from './steps/localisation-step/localisation-step.component';
import { ParametersStepComponent } from './steps/parameters-step/parameters-step.component';
import { EquationsStepComponent } from './steps/equations-step/equations-step.component';
import { GainInstancesState } from './steps/gain-formula-instances-step/gain-formula-instances-step.component';
import { selectPotentialCause } from '../../../store/selectors/potential-cause.selector';
import { selectPotentialConsequences } from '../../../store/selectors/potential-consequence.selector';
import { selectCorrectiveAction } from '../../../store/selectors/corrective-action.selector';

const DRAFTS_KEY = 'RULE_DRAFTS';

interface Draft {
  id: string;
  data: any;
  savedAt: number;
}

export interface RuleComponentConfig {
  rule?: Rule;
  mode: 'create' | 'edit';
  showDrafts?: boolean;
}

@Component({
  selector: 'app-modify-rule',
  templateUrl: './modify-rule.component.html',
  styleUrl: './modify-rule.component.scss',
  standalone: false,
})
export class ModifyRuleComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('localisationStep') localisationStep!: LocalisationStepComponent;
  @ViewChild('parametersStep') parametersStep!: ParametersStepComponent;
  @ViewChild('equationsStep') equationsStep!: EquationsStepComponent;

  private destroy$ = new Subject<void>();
  private loadedDraftId: string | null = null;

  public modifyRuleFormGroup: FormGroup = this.formBuilder.group({});
  public loadingUpdateRule$ = new Observable<boolean | undefined>();
  public isCreateOrUpdateRuleOk$ = new Observable<boolean>();
  public selectRuleUpdateErrorMessage$ = new Observable<string>();
  public creating$ = new Observable<boolean>();
  public createErrorMessage$ = new Observable<string>();
  public createOrUpdateErrorHtml: string = '';

  rule?: Rule;
  mode: 'create' | 'edit' = 'edit';
  showDrafts = false;

  initialValues: { [key: string]: any } = {};
  initialLabelIds: string[] = [];
  selectedLabelIds: string[] = [];
  formChanged = false;

  gainState: GainInstancesState = {
    requests: [],
    dirty: false,
    hasErrors: false,
    loaded: false,
  };

  plants: Plant[] = [];
  draftRequests: Draft[] = [];

  private currentPotentialCauses: any[] = [];
  private currentPotentialConsequences: any[] = [];
  private currentCorrectiveActions: any[] = [];

  @HostBinding('class.submitted') submitted = false;

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private formBuilder: FormBuilder,
    private store: Store,
    private actions$: Actions,
  ) {
    const componentConfig: RuleComponentConfig = this.config.data || {};
    this.rule = componentConfig.rule;
    this.mode = componentConfig.mode || (this.rule ? 'edit' : 'create');
    this.showDrafts = componentConfig.showDrafts ?? false;
  }

  ngOnInit() {
    this.setupForm();
    this.setupStoreSubscriptions();

    if (this.showDrafts) {
      this.loadDrafts();
    }

    this.initActionSubscriptions();

    this.modifyRuleFormGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkIfFormChanged());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Tab error checking
  get localisationHasErrors(): boolean {
    const fields = ['plant', 'plannerGroup', 'functionalLocation'];
    return fields.some((f) => {
      const ctrl = this.modifyRuleFormGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
  }

  get parametersHasErrors(): boolean {
    const fields = ['context', 'stage', 'criticality', 'isActive'];
    return fields.some((f) => {
      const ctrl = this.modifyRuleFormGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
  }

  get equationsHasErrors(): boolean {
    const fields = [
      'pythonCode',
      'evaluationStartDate',
      'evaluationEndDate',
      'alertThreshold',
      'toleranceThreshold',
      'minAlertThreshold',
      'minToleranceThreshold',
      'periodInMinutes',
      'piPointInterpolationIntervalInMinutes',
      'evaluationMode',
      'minEvaluationsToTriggerAlarm',
      'evaluationsToConsiderForAlarm',
    ];
    const formErrors = fields.some((f) => {
      const ctrl = this.modifyRuleFormGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
    return (
      formErrors ||
      (this.equationsStep && !this.equationsStep.isPythonCodeValid())
    );
  }

  get gainHasErrors(): boolean {
    return this.gainState.hasErrors;
  }

  get plantLocked(): boolean {
    return this.gainState.requests.length > 0;
  }

  get prodStageErrors(): string[] {
    const rawValue = this.modifyRuleFormGroup.getRawValue();
    const targetStage: Stage = rawValue.stage;

    const isProdTarget =
      targetStage === Stage.ProdManual || targetStage === Stage.ProdAutomated;

    if (!isProdTarget) return [];

    const errors: string[] = [];

    if (
      !this.hasValue(rawValue.alertThreshold) &&
      !this.hasValue(rawValue.minAlertThreshold)
    ) {
      errors.push(
        "au moins 1 seuil d'alarme (seuil d'alerte ou seuil d'alerte min.) doit être défini",
      );
    }

    if (this.currentPotentialCauses.length < 1) {
      errors.push('au moins 1 cause potentielle est requise');
    }

    if (this.currentPotentialConsequences.length < 1) {
      errors.push('au moins 1 conséquence potentielle est requise');
    }

    if (this.currentCorrectiveActions.length < 1) {
      errors.push('au moins 1 action corrective est requise');
    }

    if (!this.hasValue(rawValue.weeksBeforeFailure)) {
      errors.push('le nombre de semaines avant défaillance doit être défini');
    }

    return errors;
  }

  get prodStageValidationMessage(): string {
    const errors = this.prodStageErrors;
    if (errors.length === 0) return '';
    const rawValue = this.modifyRuleFormGroup.getRawValue();
    const stageName =
      rawValue.stage === Stage.ProdManual
        ? 'Mature - investigation'
        : 'Mature - avis automatisé';
    const originalStage = this.rule?.stage;
    const isAlreadyProd =
      originalStage === Stage.ProdManual ||
      originalStage === Stage.ProdAutomated;
    const prefix = isAlreadyProd
      ? `La règle est en stade '${stageName}' mais les conditions requises ne sont pas toutes remplies`
      : `Impossible de passer au stade '${stageName}'`;
    return `${prefix}. Les conditions suivantes ne sont pas remplies : ${errors.join(', ')}.`;
  }

  private getInitialPlantId(): string {
    return '';
  }

  private getInitialPlannerGroupId(): string {
    return '';
  }

  private resolveInitialPlantHierarchy(): void {
    if (
      this.mode !== 'edit' ||
      !this.rule?.functionalLocation?.sapWorkCenterId ||
      this.plants.length === 0
    )
      return;
    const workCenterId = this.rule.functionalLocation.sapWorkCenterId;
    for (const plant of this.plants) {
      for (const pg of plant.plannerGroups || []) {
        for (const wc of pg.workCenters || []) {
          if (wc.sapId === workCenterId) {
            const currentPlant = this.modifyRuleFormGroup.get('plant')?.value;
            if (!currentPlant) {
              this.modifyRuleFormGroup.patchValue({
                plant: plant.sapId,
                plannerGroup: pg.sapId,
                workCenter: wc.sapId,
              });
            }
            return;
          }
        }
      }
    }
  }

  private setupForm(): void {
    if (this.mode === 'edit' && !this.rule) {
      console.error('Rule is required for edit mode');
      return;
    }

    const formControls: any = {
      // Localisation fields
      plant: [this.getInitialPlantId(), Validators.required],
      plannerGroup: [this.getInitialPlannerGroupId(), Validators.required],
      workCenter: [
        this.rule?.functionalLocation?.sapWorkCenterId || '',
        Validators.required,
      ],
      functionalLocation: [
        this.rule?.functionalLocationId || '',
        Validators.required,
      ],
      // Parameter fields
      criticality: [
        this.rule?.criticality || '',
        [
          Validators.required,
          this.criticalityLevelValidator(
            criticalityOptions.map((o) => o.value) as CriticalityLevel[],
          ),
        ],
      ],
      ...this.createNumericControls(),
      ...this.createTextControls(),
      stage: [
        {
          value: this.rule?.stage !== undefined ? this.rule.stage : Stage.Dev,
          disabled: this.hasActiveEvaluation || this.mode === 'create',
        },
        Validators.required,
      ],
      isActive: [
        {
          value: this.rule?.isActive !== undefined ? this.rule.isActive : true,
          disabled: false,
        },
        Validators.required,
      ],
    };

    if (this.mode === 'edit') {
      formControls.uid = [{ value: this.rule!.uid, disabled: true }];
    }

    // Equation fields
    formControls.evaluationStartDate = [
      computeThresholdDate(TIME_FRAME.THREEMONTH),
      [Validators.required, this.dateRangeValidator()],
    ];
    formControls.evaluationEndDate = [
      new Date(),
      [Validators.required, this.dateRangeValidator()],
    ];

    this.modifyRuleFormGroup = this.formBuilder.group(formControls);
    this.initialValues = this.modifyRuleFormGroup.getRawValue();
  }

  private get hasActiveEvaluation(): boolean {
    if (
      !this.rule?.latestAlarm ||
      this.rule?.latestAlarm.status == 0 ||
      !this.rule.latestAlarm.evaluation
    ) {
      return false;
    }
    return this.rule.latestAlarm.evaluation.isFinal === false;
  }

  private createNumericControls() {
    return {
      alertThreshold: [
        { value: this.rule?.alertThreshold ?? null, disabled: false },
      ],
      toleranceThreshold: [
        { value: this.rule?.toleranceThreshold ?? null, disabled: false },
      ],
      minAlertThreshold: [
        { value: this.rule?.minAlertThreshold ?? null, disabled: false },
      ],
      minToleranceThreshold: [
        { value: this.rule?.minToleranceThreshold ?? null, disabled: false },
      ],
      periodInMinutes: [
        { value: this.rule?.periodInMinutes || null, disabled: false },
        Validators.required,
      ],
      piPointInterpolationIntervalInMinutes: [
        {
          value: this.rule?.piPointInterpolationIntervalInMinutes ?? 10,
          disabled: false,
        },
        [Validators.min(1)],
      ],
      weeksBeforeFailure: [
        { value: this.rule?.weeksBeforeFailure || null, disabled: false },
      ],
      evaluationMode: [this.getEvaluationModeFromRule(), Validators.required],
      minEvaluationsToTriggerAlarm: [
        {
          value: this.rule?.minEvaluationsToTriggerAlarm ?? 1,
          disabled: false,
        },
        [Validators.required, Validators.min(1)],
      ],
      evaluationsToConsiderForAlarm: [
        {
          value: this.rule?.evaluationsToConsiderForAlarm ?? 1,
          disabled: false,
        },
        [Validators.required, Validators.min(1), Validators.max(9)],
      ],
    };
  }

  private createTextControls() {
    return {
      context: [
        { value: this.rule?.context || '', disabled: false },
        Validators.required,
      ],
      behaviorSummary: [
        { value: this.rule?.behaviorSummary || '', disabled: false },
      ],
      pythonCode: [
        { value: this.rule?.pythonRule || '', disabled: false },
        Validators.required,
      ],
      activationConditionPiExpression: [
        {
          value: this.rule?.activationConditionPiExpression || '',
          disabled: false,
        },
      ],
      activationConditionPythonExpression: [
        {
          value: this.rule?.activationConditionPythonExpression || '',
          disabled: false,
        },
      ],
    };
  }

  private getEvaluationModeFromRule(): string {
    if (!this.rule) return '1/1';
    const min = this.rule.minEvaluationsToTriggerAlarm ?? 1;
    const total = this.rule.evaluationsToConsiderForAlarm ?? 1;
    const ratio = `${min}/${total}`;
    if (['1/1', '2/3', '4/5'].includes(ratio)) return ratio;
    return 'manuel';
  }

  private setupStoreSubscriptions(): void {
    this.selectRuleUpdateErrorMessage$ = this.store.select(
      selectRuleUpdateErrorMessage,
    );
    this.loadingUpdateRule$ = this.store.select(selectLoadingUpdateRule);
    this.isCreateOrUpdateRuleOk$ = this.store.select(
      selectIsCreateOrUpdateRuleOk,
    );
    this.creating$ = this.store.select(selectRuleCreating);
    this.createErrorMessage$ = this.store.select(selectRuleCreateErrorMessage);

    this.createErrorMessage$.pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      this.createOrUpdateErrorHtml = this.extractPythonError(msg);
    });
    this.selectRuleUpdateErrorMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.createOrUpdateErrorHtml = this.extractPythonError(msg);
      });

    this.store
      .select(selectPlants)
      .pipe(takeUntil(this.destroy$))
      .subscribe((plants) => {
        this.plants = plants;
        this.resolveInitialPlantHierarchy();
      });

    this.store
      .select(selectPotentialCause)
      .pipe(takeUntil(this.destroy$))
      .subscribe((causes) => {
        this.currentPotentialCauses = causes ?? [];
      });

    this.store
      .select(selectPotentialConsequences)
      .pipe(takeUntil(this.destroy$))
      .subscribe((consequences) => {
        this.currentPotentialConsequences = consequences ?? [];
      });

    this.store
      .select(selectCorrectiveAction)
      .pipe(takeUntil(this.destroy$))
      .subscribe((actions) => {
        this.currentCorrectiveActions = actions ?? [];
      });
  }

  private initActionSubscriptions(): void {
    if (this.mode === 'create') {
      this.actions$
        .pipe(ofType(ruleActions.createRuleSuccess), takeUntil(this.destroy$))
        .subscribe(() => {
          this.modifyRuleFormGroup.reset();
          this.draftRequests = this.draftRequests.filter(
            (d: any) => d.id !== this.loadedDraftId,
          );
          this.loadedDraftId = null;
        });
    }
  }

  // Validators
  private criticalityLevelValidator(allowed: CriticalityLevel[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val: CriticalityLevel = control.value;
      if (val == null) return null;
      return allowed.includes(val)
        ? null
        : { invalidCriticality: { actual: val, allowed } };
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.modifyRuleFormGroup) return null;
      const startDate = this.modifyRuleFormGroup.get(
        'evaluationStartDate',
      )?.value;
      const endDate = this.modifyRuleFormGroup.get('evaluationEndDate')?.value;
      if (!startDate || !endDate) return null;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start >= end) {
        return { dateRangeInvalid: true };
      }
      const oneYearMs = 365 * 24 * 60 * 60 * 1000;
      if (end.getTime() - start.getTime() > oneYearMs) {
        return { dateRangeTooLarge: true };
      }
      return null;
    };
  }

  // Draft functionality
  private loadDrafts(): void {
    const raw = localStorage.getItem(DRAFTS_KEY);
    this.draftRequests = raw ? JSON.parse(raw) : [];
  }

  private saveDraftsToStorage(): void {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(this.draftRequests));
  }

  loadDraft(id: string): void {
    const draft = this.draftRequests.find((d: Draft) => d.id === id);
    if (!draft) return;
    this.modifyRuleFormGroup.patchValue(draft.data);
    this.loadedDraftId = id;
  }

  saveDraft(): void {
    const formValue = this.modifyRuleFormGroup.value;
    const now = Date.now();

    if (this.loadedDraftId) {
      this.draftRequests = this.draftRequests.map((d: any) =>
        d.id === this.loadedDraftId
          ? { ...d, data: formValue, savedAt: now }
          : d,
      );
    } else {
      const newDraft: Draft = { id: uuidv4(), data: formValue, savedAt: now };
      this.draftRequests.push(newDraft);
      this.loadedDraftId = newDraft.id;
    }

    this.saveDraftsToStorage();
    this.store.dispatch(
      showMessage({
        key: 'globalToast',
        severity: 'success',
        summary: 'Succès',
        detail: 'Brouillon enregistré.',
      }),
    );
  }

  deleteDraft(id: string): void {
    this.draftRequests = this.draftRequests.filter((d: any) => d.id !== id);
    if (this.loadedDraftId === id) {
      this.modifyRuleFormGroup.reset();
      this.loadedDraftId = null;
    }
    this.saveDraftsToStorage();
    this.loadDrafts();
  }

  resetForm(): void {
    this.modifyRuleFormGroup.reset();
    this.loadedDraftId = null;
  }

  // Draft display helpers
  getPlantLabel(value: string): string | undefined {
    const opt = this.plants.find((p) => p.sapId === value);
    return opt ? opt.sapDescription || opt.sapId : '';
  }

  getPlannerGroupLabel(plantId: string, plannerGroupId: string): string {
    if (!plantId || !plannerGroupId) return '';
    const plant = this.plants.find((p) => p.sapId === plantId);
    if (plant && plant.plannerGroups) {
      const pg = plant.plannerGroups.find((pg) => pg.sapId === plannerGroupId);
      return pg ? pg.sapDescription || pg.sapId : '';
    }
    return '';
  }

  getFunctionalLocationLabel(value: string): string | undefined {
    return value;
  }

  // Change detection
  checkIfFormChanged(): void {
    const currentValues = this.modifyRuleFormGroup.getRawValue();
    const formValuesChanged = Object.keys(this.initialValues).some(
      (key) => this.initialValues[key] !== currentValues[key],
    );
    const labelsChanged = this.hasLabelsChanged();
    this.formChanged =
      formValuesChanged || labelsChanged || this.gainState.dirty;
  }

  onGainStateChange(state: GainInstancesState): void {
    this.gainState = state;

    // The plant cannot change while gain formula instances exist.
    const plant = this.modifyRuleFormGroup.get('plant');
    if (plant) {
      if (this.plantLocked && plant.enabled) {
        plant.disable({ emitEvent: false });
      } else if (!this.plantLocked && plant.disabled) {
        plant.enable({ emitEvent: false });
      }
    }

    this.checkIfFormChanged();
  }

  private hasLabelsChanged(): boolean {
    if (this.initialLabelIds.length !== this.selectedLabelIds.length)
      return true;
    const sortedInitial = [...this.initialLabelIds].sort();
    const sortedCurrent = [...this.selectedLabelIds].sort();
    return sortedInitial.some((id, index) => id !== sortedCurrent[index]);
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private extractPythonError(message: string): string {
    if (
      !message ||
      !message.includes(ERROR_KEYS.PYTHON_EXPRESSION_TEST_FAILED)
    ) {
      return '';
    }
    return message.replace(ERROR_KEYS.PYTHON_EXPRESSION_TEST_FAILED, '').trim();
  }

  // Form validation & submit
  isFormValid(): boolean {
    return (
      this.modifyRuleFormGroup.valid &&
      !this.gainState.hasErrors &&
      (!this.equationsStep || this.equationsStep.isPythonCodeValid()) &&
      this.prodStageErrors.length === 0
    );
  }

  markAllTouched(): void {
    Object.keys(this.modifyRuleFormGroup.controls).forEach((key) => {
      this.modifyRuleFormGroup.get(key)?.markAsTouched();
    });
  }

  onSubmit(): void {
    this.markAllTouched();
    if (!this.isFormValid()) return;
    this.submitted = true;

    const formValue = { ...this.modifyRuleFormGroup.value };

    if (this.mode === 'edit') {
      if (!this.rule) return;

      const updateRuleRequest: UpdateRuleRequest = {
        uid: this.rule.uid,
        functionalLocationId:
          formValue.functionalLocation ||
          this.rule.functionalLocationId ||
          null,
        context: formValue.context || this.rule.context || '',
        isActive: formValue.isActive,
        pythonRule: formValue.pythonCode || null,
        criticality: formValue.criticality!,
        piPointsDataLookbackWindowInMinutes: formValue.periodInMinutes!,
        alertThreshold: formValue.alertThreshold ?? undefined,
        toleranceThreshold: formValue.toleranceThreshold ?? undefined,
        minAlertThreshold: formValue.minAlertThreshold ?? undefined,
        minToleranceThreshold: formValue.minToleranceThreshold ?? undefined,
        periodInMinutes: formValue.periodInMinutes!,
        ownedBy: this.rule.ownedBy ?? null,
        stage: formValue.stage,
        weeksBeforeFailure: formValue.weeksBeforeFailure,
        activationConditionPythonExpression:
          formValue.activationConditionPythonExpression?.trim() || undefined,
        behaviorSummary: formValue.behaviorSummary || '',
        evaluationsToConsiderForAlarm:
          formValue.evaluationsToConsiderForAlarm ?? null,
        minEvaluationsToTriggerAlarm:
          formValue.minEvaluationsToTriggerAlarm ?? null,
        piPointInterpolationIntervalInMinutes:
          formValue.piPointInterpolationIntervalInMinutes ?? null,
        workCenterLabelFKs: this.selectedLabelIds,
        gainFormulaInstances: this.gainState.loaded
          ? this.gainState.requests
          : null,
      };
      this.store.dispatch(ruleActions.updateRule({ updateRuleRequest }));
    } else {
      const { title, ...formValueWithoutTitle } = formValue;

      const createRuleRequest: CreateRuleRequest = {
        functionalLocationId: formValueWithoutTitle.functionalLocation || null,
        context: formValueWithoutTitle.context,
        isActive: formValueWithoutTitle.isActive,
        formula: formValueWithoutTitle.formula,
        behaviorSummary: formValueWithoutTitle.behaviorSummary || undefined,
        pythonRule: formValueWithoutTitle.pythonCode || null,
        criticality: formValueWithoutTitle.criticality,
        activationConditionPythonExpression:
          formValueWithoutTitle.activationConditionPythonExpression?.trim() ||
          null,
        piPointsDataLookbackWindowInMinutes:
          formValueWithoutTitle.periodInMinutes,
        alertThreshold: formValueWithoutTitle.alertThreshold ?? undefined,
        toleranceThreshold:
          formValueWithoutTitle.toleranceThreshold ?? undefined,
        minAlertThreshold: formValueWithoutTitle.minAlertThreshold ?? undefined,
        minToleranceThreshold:
          formValueWithoutTitle.minToleranceThreshold ?? undefined,
        periodInMinutes: formValueWithoutTitle.periodInMinutes,
        activationConditionPiExpression:
          formValueWithoutTitle.activationConditionPiExpression?.trim() || null,
        evaluationsToConsiderForAlarm:
          formValueWithoutTitle.evaluationsToConsiderForAlarm ?? null,
        minEvaluationsToTriggerAlarm:
          formValueWithoutTitle.minEvaluationsToTriggerAlarm ?? null,
        piPointInterpolationIntervalInMinutes:
          formValueWithoutTitle.piPointInterpolationIntervalInMinutes ?? null,
        workCenterLabelFKs: this.selectedLabelIds,
        gainFormulaInstances: this.gainState.requests,
      };

      this.store.dispatch(ruleActions.createRule({ createRuleRequest }));
    }
  }

  onLabelSelectionChange(ids: string[]): void {
    this.selectedLabelIds = ids;
    this.checkIfFormChanged();
  }

  private readonly stepFields: Record<number, string[]> = {
    0: ['plant', 'plannerGroup', 'workCenter', 'functionalLocation'],
    1: ['context', 'stage', 'criticality', 'isActive'],
    2: [
      'pythonCode',
      'evaluationStartDate',
      'evaluationEndDate',
      'periodInMinutes',
      'evaluationMode',
      'minEvaluationsToTriggerAlarm',
      'evaluationsToConsiderForAlarm',
    ],
  };

  onStepChange(event: StepperSelectionEvent): void {
    // Prevent direct navigation to the final "Terminé" step (index 4) via header click
    if (event.selectedIndex === 4 && !this.submitted) {
      setTimeout(() => {
        this.stepper.selectedIndex = event.previouslySelectedIndex;
      });
      return;
    }

    // Mark fields of the departed step as touched so error indicators activate
    const fields = this.stepFields[event.previouslySelectedIndex];
    if (fields) {
      fields.forEach((f) => this.modifyRuleFormGroup.get(f)?.markAsTouched());
    }
  }

  submitForm(): void {
    const valid = this.isFormValid();
    this.onSubmit();
    if (valid) {
      this.stepper.selectedIndex = this.stepper.steps.length - 1;
    }
  }

  getSubmitTooltip(): string {
    if (this.prodStageErrors.length > 0) {
      return this.prodStageValidationMessage;
    }

    if (this.gainState.hasErrors) {
      return 'Une valeur doit être sélectionnée pour chaque paramètre des formules de gain';
    }

    const fieldLabels: { [key: string]: string } = {
      plant: 'Usine',
      plannerGroup: 'Groupe de gestion',
      functionalLocation: 'Poste technique',
      criticality: 'Criticité',
      context: 'Contexte',
      pythonCode: 'Code Python',
      periodInMinutes: 'Période (minutes)',
      evaluationMode: "Mode d'évaluation",
      minEvaluationsToTriggerAlarm: 'Évaluations min. pour déclencher',
      evaluationsToConsiderForAlarm: 'Évaluations à considérer',
    };

    const invalidFields: string[] = Object.entries(fieldLabels)
      .filter(([key]) => {
        const ctrl = this.modifyRuleFormGroup.get(key);
        return ctrl && ctrl.invalid;
      })
      .map(([, label]) => label);

    if (this.equationsStep && !this.equationsStep.isPythonCodeValid()) {
      if (!invalidFields.includes('Code Python')) {
        invalidFields.push('Code Python (invalide)');
      }
    }

    return invalidFields.length > 0
      ? `Champs requis manquants : ${invalidFields.join(', ')}`
      : this.mode === 'edit' && !this.formChanged
        ? 'Aucune modification détectée'
        : '';
  }

  closeDialog(): void {
    this.ref.close();
  }

  goBackToPreviousStep(): void {
    if (this.stepper && this.stepper.selectedIndex > 0) {
      this.stepper.selectedIndex = this.stepper.selectedIndex - 1;
    }
  }
}
